import { forwardRef, useEffect, useRef } from "react";

const usePointerMotion = (mode) => {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const canHover = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!canHover.matches || reducedMotion.matches) return undefined;

    let resetTimer;
    const move = (event) => {
      const rect = element.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      const transform = mode === "tilt"
        ? `perspective(900px) rotateX(${-y * 7}deg) rotateY(${x * 7}deg) translateZ(0)`
        : `translate3d(${x * 10}px, ${y * 10}px, 0)`;
      element.style.transition = "transform 90ms ease-out, box-shadow 220ms ease";
      element.style.transform = transform;
    };
    const leave = () => {
      element.style.transition = "transform 420ms cubic-bezier(.2,.75,.25,1), box-shadow 220ms ease";
      element.style.transform = mode === "tilt" ? "perspective(900px) rotateX(0) rotateY(0)" : "translate3d(0,0,0)";
      resetTimer = window.setTimeout(() => {
        element.style.removeProperty("transform");
        element.style.removeProperty("transition");
      }, 430);
    };

    element.addEventListener("pointermove", move);
    element.addEventListener("pointerleave", leave);
    return () => {
      clearTimeout(resetTimer);
      element.removeEventListener("pointermove", move);
      element.removeEventListener("pointerleave", leave);
    };
  }, [mode]);

  return ref;
};

export const TiltSurface = ({ as: Component = "div", ...props }) => {
  const ref = usePointerMotion("tilt");
  return <Component ref={ref} {...props} />;
};

export const MagneticButton = forwardRef(({ as: Component = "button", ...props }, forwardedRef) => {
  const motionRef = usePointerMotion("magnetic");
  const setRef = (node) => {
    motionRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };
  return <Component ref={setRef} {...props} />;
});

MagneticButton.displayName = "MagneticButton";
